import { ApplicationConfig, importProvidersFrom, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { routes } from './app.routes';
import { SecureStorageModule } from '@common/secure-storage';
import { environment } from '../environments/environment';
import { HeadersInterceptor } from './interceptor/headers.interceptor';
import { AuthGuardService } from './shared/onelogin/auth-guard.service';
import {
  SecureCommInterceptor,
  KeyExchangeService,
  KeyStorageService,
  SECURE_COMM_CONFIG
} from '@secure-comm/angular';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';

// Secure-Comm Gateway Configuration
const secureComm = {
  apiBaseUrl: environment.secureComm.apiBaseUrl, // URL base del Gateway en AKS
  keyExchangeEndpoint: '/crypto/key-exchange',
  encryptAll: false, // Only encrypt URLs matching encryptPatterns (more explicit)
  encryptPatterns: [
    /\/cm-gateway-secure-comm.*\/api\/.*/,  // Only encrypt Gateway URLs with /api/ path
  ],
  excludePatterns: [
    // Exceptions to encryptPatterns (URLs that match encryptPatterns but should NOT be encrypted)
    /\/crypto\/key-exchange/,  // Key exchange must be unencrypted
    /\/crypto\/health/,         // Health checks
    /\/actuator\/.*/,           // Spring actuator endpoints
    /\/assets\/.*/,             // Static assets
    /\.json$/,                  // i18n translation files
  ],
  keyRotationHours: 24,
  storageType: 'session' as const,
  debug: !environment.production,
  maxTimestampSkew: 3600000, // 60 minutes in milliseconds - CRÍTICO para AKS time differences
};

// Secure-Comm Factories
export function keyExchangeServiceFactory(
  http: HttpClient,
  storage: KeyStorageService,
  config: any
): KeyExchangeService {
  console.log(
    '🔧 [Factory] Initializing KeyExchangeService with injected config:',
    {
      hasApiBaseUrl: !!config.apiBaseUrl,
      keyExchangeEndpoint: config.keyExchangeEndpoint,
      encryptAll: config.encryptAll,
      maxTimestampSkew: config.maxTimestampSkew,
      debug: config.debug,
      fullConfig: config,
    }
  );
  const service = new KeyExchangeService(http, storage);
  service.initialize(config);
  console.log('🔧 [Factory] Service initialized');
  return service;
}

export function secureCommInterceptorFactory(
  keyExchange: KeyExchangeService,
  config: any
): SecureCommInterceptor {
  console.log(
    '🔧 [Factory] Initializing SecureCommInterceptor with injected config:',
    {
      hasApiBaseUrl: !!config.apiBaseUrl,
      encryptAll: config.encryptAll,
      maxTimestampSkew: config.maxTimestampSkew,
    }
  );
  const interceptor = new SecureCommInterceptor(keyExchange);
  interceptor.initialize(config);
  console.log('🔧 [Factory] Interceptor initialized');
  return interceptor;
}

export function initializeSecureComm(
  storage: KeyStorageService,
  keyExchange: KeyExchangeService
) {
  return async () => {
    await storage.initialize(secureComm.storageType);

    try {
      console.log('🔑 Attempting key exchange with AKS gateway...');
      await keyExchange.performKeyExchange();
      console.log('✅ Key exchange successful');
    } catch (error: any) {
      console.warn(
        '⚠️ Key exchange failed, but continuing anyway:',
        error.message
      );
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(
      withInterceptorsFromDi()
    ),

    // Secure-Comm Configuration
    {
      provide: SECURE_COMM_CONFIG,
      useValue: secureComm,
    },

    // Secure-Comm Services
    KeyStorageService,
    {
      provide: KeyExchangeService,
      useFactory: keyExchangeServiceFactory,
      deps: [HttpClient, KeyStorageService, SECURE_COMM_CONFIG],
    },

    // HTTP Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: HeadersInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: secureCommInterceptorFactory,
      deps: [KeyExchangeService, SECURE_COMM_CONFIG],
      multi: true,
    },

    // APP Initializer for Secure-Comm
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSecureComm,
      deps: [KeyStorageService, KeyExchangeService],
      multi: true,
    },

    AuthGuardService,
    importProvidersFrom(
      SecureStorageModule.forRoot({
        ...environment.secureStorage,
        enabled: String(environment.secureStorage.enabled).toLowerCase() === 'true'
      })
    ),
    providePrimeNG({
      theme: {
        preset: Lara, // Aplica automáticamente el tema Lara
        // options: { color: '#4666e5', contrast: 'high' } // opcional para personalización
        options: {
              darkModeSelector: 'none'
        }
      },
    }),
  ]
};
